import {useMemo, useState} from 'react';
import TaskCard from '../task-card/task-card';
import {Box, ListItem} from '@mui/material';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {ActivitiesGroup} from '../../../backend/interfaces/activities-group';
import {CarePlan} from '../../../backend/interfaces/care-plan';
import {CarePlanConfiguration} from '../../../backend/interfaces/care-plan-configuration';
import {EntitySocialResponse} from '../../../backend/interfaces/entity-social-response';
import {EntityUser} from '../../../backend/responses/entity-user';
import {CarePlanActivitiesGroupApiRequestConfigurationsItem} from '../../../backend/requests/care-plan-activities-group';
import {EntityUserCarePlansRepository} from '../../../repositories/entity-user-care-plans';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {ConfigurationsIndex, ConfigurationsValuesIndex} from '../../../types';
import {InteractionEvent} from '../pressable';
import {convertArrayToIndex} from '../../../utils/functions';
import {CarePlanActivitiesGroupType} from '../../../backend/interfaces/care-plan-activities-group-type';
import {ActivityType} from '../../../backend/interfaces/activity-type';

interface TaskTableItemProps {
  onClick?: (event: InteractionEvent) => void;
  onLongPress?: (event: InteractionEvent) => void;
  entityId: string;
  displayType: 'user' | 'task';
  task: CareTaskComplementary;
  senior?: EntityUser;
  entitySocialResponses?: Array<EntitySocialResponse>;
  hideAvatar?: boolean;
}

export default function TaskTableItem({
  onClick,
  onLongPress,
  entityId,
  task,
  senior,
  entitySocialResponses,
  displayType,
  hideAvatar = false,
}: TaskTableItemProps) {
  const [activitiesGroup, setActivitiesGroup] = useState<ActivitiesGroup | undefined>(undefined);
  const [configurationsIndex, setConfigurationsIndex] = useState<ConfigurationsIndex>(undefined);
  const [configurationsValuesIndex, setConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [taskConfigurations, setTaskConfigurations] = useState<
    Array<CarePlanConfiguration> | undefined
  >(undefined);
  const [taskConfigurationsValuesIndex, setTaskConfigurationsValuesIndex] =
    useState<ConfigurationsValuesIndex>(undefined);
  const [types, setTypes] = useState<ActivityType>();

  const seniorSocialResponses = useMemo(() => {
    if (
      entitySocialResponses &&
      entitySocialResponses?.length > 0 &&
      senior?.affiliation?.socialResponses &&
      senior?.affiliation?.socialResponses?.length > 0
    ) {
      return senior.affiliation.socialResponses.map((socialResponseId) => {
        return entitySocialResponses.find(
          (socialResponse) => socialResponse.id === socialResponseId
        );
      });
    } else {
      return [];
    }
  }, [entitySocialResponses, senior?.affiliation]);

  const onCardCollapseChange = (expanded: boolean) => {
    if (!expanded) return;
    if (!activitiesGroup || !configurationsIndex || !configurationsValuesIndex) {
      Promise.all<
        [
          Promise<CarePlan | null>,
          Promise<ActivitiesGroup | null>,
          Promise<Array<CarePlanConfiguration>>,
          Promise<Array<CarePlanConfiguration>>,
          Promise<ActivityType | null>,
        ]
      >([
        // Load Careplan
        EntityUserCarePlansRepository.instance.getCarePlan(
          entityId,
          task.careTask.userId,
          task.careTask.activitiesSectorId
        ),
        // Load Activities Group (to obtain the Configuration layout)
        EntityActivitiesRepository.instance.getGroup(
          entityId,
          task.careTask.activitiesSectorId,
          task.careTask.activitiesGroupId
        ),
        // Load Activity Type Configurations (to list the configuration values)
        EntityActivitiesRepository.instance.getTypeCarePlanConfigurations(
          entityId,
          task.careTask.activitiesSectorId,
          task.careTask.activitiesGroupId,
          task.careTask.activityTypeId
        ),
        // Load Tasks Group Configurations (to list the configuration values)
        EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
          entityId,
          task.careTask.activitiesSectorId,
          task.careTask.activitiesGroupId
        ),
        EntityActivitiesRepository.instance.getType(
          entityId,
          task.careTask.activitiesSectorId,
          task.careTask.activitiesGroupId,
          task.careTask.activityTypeId
        ),
      ]).then(
        ([
          carePlan,
          activitiesGroup,
          typePlanConfiguration,
          groupPlanConfiguration,
          activityTypes,
        ]: [
          CarePlan | null,
          ActivitiesGroup | null,
          Array<CarePlanConfiguration>,
          Array<CarePlanConfiguration>,
          ActivityType | null,
        ]) => {
          if (activitiesGroup) {
            const entry = carePlan?.activitiesGroups?.find(
              (it) => it.activitiesGroupId === activitiesGroup.id
            );

            const carePlanActivityType: CarePlanActivitiesGroupType | undefined =
              entry?.activityTypes?.find((it) => it.id === task.careTask.activityTypeEntryId);

            const typeConfigurationValuesIndex =
              carePlanActivityType?.configurations &&
              convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
                carePlanActivityType.configurations,
                'carePlanConfigurationId'
              );

            // Build values index
            const valuesIndex =
              convertArrayToIndex<CarePlanActivitiesGroupApiRequestConfigurationsItem>(
                entry?.configurations ?? [],
                'carePlanConfigurationId'
              );

            // Build Configurations Index
            const configurationsIndex = convertArrayToIndex<CarePlanConfiguration>(
              groupPlanConfiguration,
              'id'
            );
            setConfigurationsIndex(configurationsIndex);

            setTaskConfigurationsValuesIndex(typeConfigurationValuesIndex);

            setActivitiesGroup(activitiesGroup);

            setConfigurationsValuesIndex(valuesIndex);

            setTaskConfigurations(typePlanConfiguration);

            setTypes(activityTypes || undefined);
          } else {
            // Not found
          }
        }
      );
    }
  };
  return (
    <ListItem sx={{p: 0, m: 0}}>
      <Box sx={{width: '100%'}}>
        <TaskCard
          senior={senior}
          displayType={displayType}
          avatarBorderColor={seniorSocialResponses[0]?.foregroundColor}
          hideAvatar={hideAvatar}
          taskDetails={task}
          activitiesGroup={activitiesGroup}
          configurationsIndex={configurationsIndex}
          configurationsValuesIndex={configurationsValuesIndex}
          taskConfigurationsValuesIndex={taskConfigurationsValuesIndex}
          taskConfigurations={taskConfigurations}
          types={types}
          onChange={onCardCollapseChange}
          onLongPress={onLongPress}
          onClick={onClick}
        />
      </Box>
    </ListItem>
  );
}
